import React, { useEffect, useCallback, useState, useRef } from 'react';
import ReactDom from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, ReferralSearchFilters } from 'types';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import PopUp, { PopUpOutFields } from './popup';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import {
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import { fetchReferralById } from 'redux/referrals';
import { useComponentMount } from 'utilities/hooks.util';
import Utils from 'utilities/helper.util';
import { MD } from 'appConstants/layout';
import { addLayerToMap, findLayerById } from 'utilities/map-instance.util';

const filterStringReducer = (accumulator: any, currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'createdAfter':
      return value
        ? `${accumulator} AND created_at > TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'createdBefore':
      return value
        ? `${accumulator} AND created_at < TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'updatedAfter':
      return value
        ? `${accumulator} AND updated_at > TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'updatedBefore':
      return value
        ? `${accumulator} AND updated_at < TIMESTAMP '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}'`
        : accumulator;
    case 'digStartAfter':
      return value
        ? `${accumulator} AND dig_start_at > DATE '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD')}'`
        : accumulator;
    case 'digStartBefore':
      return value
        ? `${accumulator} AND dig_start_at < DATE '${Utils.getNewDate(value)
            .utc()
            .format('YYYY-MM-DD')}'`
        : accumulator;
    case 'status':
      return value.length > 0
        ? `${accumulator} AND status IN (${value
            .map((s: string) => `'${s}'`)
            .join(',')})`
        : accumulator;
    case 'requestorFullName':
      return value
        ? `${accumulator} AND UPPER(enquiry_user_name) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'requestorEmail':
      return value
        ? `${accumulator} AND UPPER(enquiry_user_email) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'externalId':
      return value ? `${accumulator} AND external_id = ${value}` : accumulator;
    case 'enqExternalId':
      return value
        ? `${accumulator} AND enquiry_external_id = ${value}`
        : accumulator;
    default:
      return accumulator;
  }
};

const MapView: React.FC<Props> = ({
  searchFilter,
  referralsLayerExtent,
  selectedId,
  isMapVisible,
  baseMapTheme,
  user,
  mapSearchWithExtentZoom
}) => {
  const mapSearchWithExtentZoomRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const featureLayerRef = useRef<any>(null);
  const extraMapModules = [
    MapSettings.modules.featureLayer,
    MapSettings.modules.polygon
  ];
  const dispatch: any = useDispatch();

  const clickDetailButton = useCallback(
    (referral: any) => {
      const { loadedView } = window;
      const {
        enquiry_id: enquiryId,
        objectid: referralId,
        external_id: externalId
      } = referral;
      loadedView.closePopup();
      const payload = {
        enquiryId,
        referralId,
        returnResponses: true,
        returnGeometry: true
      };
      selectedId(externalId);
      dispatch(fetchReferralById(payload));
    },
    [dispatch, selectedId]
  );

  const onMapReady = useCallback(() => {
    const { loadedView, loadedMap, loadedMapModules } = window;
    if (loadedMap) {
      const { Config, FeatureLayer } = loadedMapModules;

      // Only add interceptor once
      const interceptorExists = Config.request.interceptors.some(
        (interceptor: any) =>
          interceptor.urls === getFeatureServerUrl('Referrals')
      );

      if (!interceptorExists) {
        Config.request.interceptors.push({
          urls: getFeatureServerUrl('Referrals'),
          before: featureRequestInterceptors
        });
      }

      const popupTemplate = {
        content: (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <PopUp
              attributes={feature?.graphic?.attributes || null}
              onButtonClick={clickDetailButton}
            />,
            node
          );
          return node;
        },
        outFields: PopUpOutFields,
        overwriteActions: true
      };

      // Check if layer already exists in the map or in our ref
      let featureLayer: any = findLayerById('referrals-feature-layer');

      if (!featureLayer && !featureLayerRef.current) {
        // Create the layer only once and store it in ref
        featureLayer = new FeatureLayer({
          id: 'referrals-feature-layer',
          url: getFeatureServerUrl('Referrals'),
          outFields: ['objectid'],
          minScale: MapSettings.shapeMinScale,
          popupTemplate
        });

        featureLayer.visible = false;
        featureLayerRef.current = featureLayer;
      } else if (!featureLayer && featureLayerRef.current) {
        // Layer was created before but not added to map, use the stored instance
        featureLayer = featureLayerRef.current;
      }

      // Always update popupTemplate to ensure it uses the latest clickDetailButton closure
      // This fixes the stale closure issue where the popup holds a reference to an unmounted component
      if (featureLayer) {
        featureLayer.popupTemplate = popupTemplate;
      }

      // Try to add the layer if it's not already in the map
      if (featureLayer && !findLayerById('referrals-feature-layer')) {
        addLayerToMap(featureLayer);
      }

      loadedView.popup.visibleElements = {
        featureNavigation: false,
        closeButton: false
      };
      loadedView.popup.actions = null;
      loadedView.popup.dockOptions = {
        buttonEnabled: false,
        breakpoint: {
          width: MD
        },
        position: 'bottom-center'
      };

      loadedView.popup.alignment = () => {
        const popupY = loadedView.popup?.location?.y;
        const mapY = loadedView.center?.y;
        if (popupY && mapY) {
          return popupY > mapY ? 'bottom-center' : 'top-center';
        }

        return 'auto';
      };
    }
    setIsMapLoaded(true);
  }, [clickDetailButton]);

  const setDefinitionExpression = useCallback(
    (filter: ReferralSearchFilters, organisationId: number) => {
      const { loadedView, loadedMapModules } = window;

      // Use centralized utility to find layer from the correct map instance
      const featureLayer: any = findLayerById('referrals-feature-layer');

      if (featureLayer && loadedView && loadedMapModules) {
        const filterArr = Object.entries(filter);
        const definitionExpression = filterArr.reduce(
          filterStringReducer,
          `organisation_id = ${organisationId} AND is_sandbox_test = false`
        );

        loadedView?.popup && loadedView.closePopup();
        featureLayer.visible = true;
        featureLayer.definitionExpression = definitionExpression;
        if (!mapSearchWithExtentZoomRef.current) {
          const { Polygon } = loadedMapModules;
          dispatch(
            queryFeatureLayerExtentStart(
              featureLayer,
              definitionExpression,
              Polygon.fromExtent(loadedView.extent)
            )
          );
        } else {
          dispatch(queryFeatureLayerExtentStart(featureLayer));
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (referralsLayerExtent) {
      if (typeof referralsLayerExtent !== 'string') {
        if (referralsLayerExtent?.noRecord) {
          dispatch(addAlert('map-alert', 'info', 'No record found'));
        } else {
          // Only zoom if user explicitly clicked zoom button, not on initial load
          if (mapSearchWithExtentZoom[0] && !isInitialLoadRef.current) {
            zoomToFeature(referralsLayerExtent);
          }
          // Mark that initial load is complete after first extent is received
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        }
      }
    }
  }, [referralsLayerExtent, dispatch, mapSearchWithExtentZoom]);

  useEffect(() => {
    mapSearchWithExtentZoomRef.current = mapSearchWithExtentZoom[0];
    user?.organisationId &&
      isMapLoaded &&
      isMapVisible &&
      searchFilter &&
      setDefinitionExpression(searchFilter, user.organisationId);
  }, [
    user,
    isMapLoaded,
    isMapVisible,
    searchFilter,
    setDefinitionExpression,
    mapSearchWithExtentZoom
  ]);

  useEffect(() => {
    if (isMapLoaded && baseMapTheme) {
      const { polygonDarkSymbol, polygonSymbol } = MapSettings;

      // Use centralized utility to find layer from the correct map instance
      const layer: any = findLayerById('referrals-feature-layer');
      if (layer) {
        layer.renderer = {
          type: 'simple',
          symbol: baseMapTheme === 'dark' ? polygonDarkSymbol : polygonSymbol
        };
      }
    }
  }, [baseMapTheme, isMapLoaded]);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup('map-alert'));
      dispatch(clearFeatureLayerLayerExtent());
      featureLayerRef.current = null; // Clean up ref on unmount
    };
  });

  return (
    <div
      className="history-page-map--view"
      data-testid="referralHistoryMapView"
    >
      <Map
        extraModules={extraMapModules}
        onReady={onMapReady}
        showSearchButton={true}
        showbaseMapButton={true}
      />
    </div>
  );
};

const mapStateToProps = ({
  mapCache,
  session,
  referralsCache,
  app
}: RootState) => ({
  referralsLayerExtent: mapCache.featureLayerExtent,
  searchFilter: referralsCache.data.filters,
  user: session?.currentUser,
  baseMapTheme: app?.baseMapTheme,
  mapSearchWithExtentZoom: mapCache.mapSearchWithExtentZoom
});

const connector = connect(mapStateToProps);

type ownProps = {
  selectedId: (val: number) => void;
  isMapVisible: boolean;
};

type Props = CFCProps<typeof connector> & ownProps;

MapView.displayName = 'ReferralsHistoryMapView';

export default connector(MapView);
